import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, forwardRef } from '@angular/core';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { FocusFieldDirective } from '@games/core/focus';
import { DimensionsListenerDirective } from '@games/shared/game-dynamic-blocks/dimensions-listener.directive';
import { MathUtils } from '../../utils';
import { MathOperationWritable } from '../math-operation';
import { InputWithCalculatorDirective } from '@games/core/focus/InputWithCalculator';
import { VirtualKeyboardDirective } from '@games/core/input/VirtualKeyboard';

@Component({
  selector: 'ms-fraction-svg, ms-math-fraction',
  templateUrl: './fraction-svg.component.html',
  styleUrls: ['./fraction-svg.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FocusFieldDirective,
    VirtualKeyboardDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FractionSvgComponent),
      multi: true,
    },
  ],
  hostDirectives: [DimensionsListenerDirective],
})
export class FractionSvgComponent extends MathOperationWritable implements OnChanges {
  @Input()
  public get numerator(): string {
    return this._numerator;
  }

  public set numerator(value: string) {
    this._numerator = value;
    this.updateViewBox();
    // this.numeratorControl.setValue(value);
  }

  private _numerator: string = '';

  @Input()
  public get denominator(): string {
    return this._denominator;
  }

  public set denominator(value: string) {
    this._denominator = value;
    this.updateViewBox();
    // this.denominatorControl.setValue(value);
  }

  private _denominator: string = '';

  @Input()
  public get outside(): string {
    return this._outside;
  }

  public set outside(value: string) {
    this._outside = value;
    this.updateViewBox();
    // this.outsideControl.setValue(value);
  }

  private _outside: string = '';

  @Input() alwaysShowOutside: boolean = false;

  @Input()
  public get useTextInputNumerator(): boolean {
    return this._useTextInputNumerator;
  }

  public set useTextInputNumerator(value: boolean) {
    this._useTextInputNumerator = value;
    if (value) {
      this.numerator = this.numeratorControl.value;
      this.updateViewBox();
    }
  }

  private _useTextInputNumerator: boolean = false;

  @Input()
  public get useTextInputDenominator(): boolean {
    return this._useTextInputDenominator;
  }

  public set useTextInputDenominator(value: boolean) {
    this._useTextInputDenominator = value;
    if (value) {
      this.denominator = this.denominatorControl.value;
      this.updateViewBox();
    }
  }

  private _useTextInputDenominator: boolean = false;

  @Input()
  public get useTextInputOutside(): boolean {
    return this._useTextInputOutside;
  }

  public set useTextInputOutside(value: boolean) {
    this._useTextInputOutside = value;
    if (value) {
      this.outside = this.outsideControl.value;
      this.updateViewBox();
    }
  }

  @Input() stringifyAnswer: boolean = false;

  private _useTextInputOutside: boolean = false;

  readonly numeratorControl = new FormControl<string>('');

  readonly denominatorControl = new FormControl<string>('');

  readonly outsideControl = new FormControl<string>('');

  padding: number = 10;

  strokeWidth: number = 2;

  outsideWidth: number = 0;

  outsideMargin: number = 15;

  fractionWidth: number = 100;

  constructor() {
    super();
    this.numeratorControl.valueChanges.subscribe((value) => {
      if (!this.useTextInputNumerator) {
        this.numerator = value;
      }
      this.updateViewBox();
      this.emitChanges();
    });

    this.denominatorControl.valueChanges.subscribe((value) => {
      if (!this.useTextInputDenominator) {
        this.denominator = value;
      }
      this.updateViewBox();
      this.emitChanges();
    });

    this.outsideControl.valueChanges.subscribe((value) => {
      if (!this.useTextInputOutside) {
        this.outside = value;
      }
      this.updateViewBox();
      this.emitChanges();
    });
  }

  ngOnChanges(): void {
    this.updateViewBox();
  }

  writeValue(value: string | [string, string, string] | null): void {
    if (!value) return;

    let outside = '';
    let numerator = '';
    let denominator = '';

    if (typeof value === 'string') {
      // Парсим строку формата "outside numerator/denominator" или "numerator/denominator"
      const parts = value.split(' ');
      if (parts.length === 2) {
        // Формат: "outside numerator/denominator"
        outside = parts[0];
        const fractionParts = parts[1].split('/');
        numerator = fractionParts[0] || '';
        denominator = fractionParts[1] || '';
      } else {
        // Формат: "numerator/denominator"
        const fractionParts = value.split('/');
        numerator = fractionParts[0] || '';
        denominator = fractionParts[1] || '';
      }
    } else if (Array.isArray(value)) {
      // Массив формата [outside, numerator, denominator]
      [outside, numerator, denominator] = value;
    }

    let changes = false;
    if (this.numerator !== numerator) {
      if (!this.useTextInputNumerator) {
        this.numerator = numerator;
      }
      this.numeratorControl.setValue(numerator.toString(), { emitEvent: false });
      changes = true;
    }
    if (this.denominator !== denominator) {
      if (!this.useTextInputDenominator) {
        this.denominator = denominator;
      }
      this.denominatorControl.setValue(denominator.toString(), { emitEvent: false });
      changes = true;
    }
    if (this.outside !== outside) {
      if (!this.useTextInputOutside) {
        this.outside = outside;
      }
      this.outsideControl.setValue(outside.toString(), { emitEvent: false });
      changes = true;
    }
    if (changes) {
      this.updateViewBox();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.numeratorControl.disable();
      this.denominatorControl.disable();
      this.outsideControl.disable();
    } else {
      this.numeratorControl.enable();
      this.denominatorControl.enable();
      this.outsideControl.enable();
    }
  }

  private updateViewBox(): void {
    const numeratorText = this.useTextInputNumerator
      ? this.numeratorControl.value || ''
      : this.numerator.toString();
    const denominatorText = this.useTextInputDenominator
      ? this.denominatorControl.value || ''
      : this.denominator.toString();
    const outsideText = this.useTextInputOutside
      ? this.outsideControl.value || ''
      : this.outside.toString();

    const numeratorWidth = Math.max(
      60,
      MathUtils.getTextWidth(numeratorText, {
        size: this.fontSize,
        family: this.fontFamily,
        weight: this.fontWeight,
      }),
    );
    const denominatorWidth = Math.max(
      60,
      MathUtils.getTextWidth(denominatorText, {
        size: this.fontSize,
        family: this.fontFamily,
        weight: this.fontWeight,
      }),
    );
    const isOutsideVisible =
      this.useTextInputOutside ||
      (this.outside !== '' && (String(this.outside) !== '0' || this.alwaysShowOutside));
    const outsideWidth = isOutsideVisible
      ? Math.max(
          40,
          MathUtils.getTextWidth(outsideText, {
            size: this.fontSize,
            family: this.fontFamily,
            weight: this.fontWeight,
          }),
        ) + this.outsideMargin
      : 0;

    const maxWidth = Math.max(numeratorWidth, denominatorWidth);
    const fractionWidth = maxWidth + this.padding * 2;
    this.strokeWidth = this.fontSize / 14;
    const height = this.fontSize * 2 + this.strokeWidth;

    this.outsideWidth = outsideWidth;
    this.fractionWidth = fractionWidth;

    this._setViewBox(outsideWidth + fractionWidth, height);
  }

  emitChanges() {
    const numeratorValue = this.useTextInputNumerator
      ? (this.numeratorControl.value ?? '').toString()
      : this.numerator.toString();
    const denominatorValue = this.useTextInputDenominator
      ? (this.denominatorControl.value ?? '').toString()
      : this.denominator.toString();
    const outsideValue = this.useTextInputOutside
      ? (this.outsideControl.value ?? '').toString()
      : this.outside.toString();

    // Проверяем, что числитель и знаменатель не пустые и знаменатель не равен нулю
    if (!numeratorValue || !denominatorValue || denominatorValue === '0') {
      return;
    }

    if (this.stringifyAnswer) {
      let result: string;
      if (outsideValue) {
        result = `${outsideValue} ${numeratorValue}/${denominatorValue}`;
      } else {
        result = `${numeratorValue}/${denominatorValue}`;
      }
      this.onChange(result);
    } else {
      this.onChange([outsideValue, numeratorValue, denominatorValue]);
    }
  }
}
